import { useState } from 'react';
import { sx } from '../../ui/designStyles';
import { useBuildLabels } from '../../ui/buildLabels';
import { addedMoney, clampPct, pilotHead, SHARE_DEFAULT, type PilotState, type ShareConfig } from './pilot';

const NOT_MEASURED = 'not measured yet';
const $ = (usd: number) => '$' + Math.round(usd).toLocaleString('en-US');

/**
 * Treasury's "What you pay Aithora" during the free pilot (design Finance · Facility, L11942-11954):
 * the pilot line, what Aithora made the building (the same lines as Billing), and when pricing
 * starts. No past bills: nothing is billed during the pilot.
 */
export function PilotPayBlock({ pilot }: { pilot: PilotState }) {
  const added = addedMoney();
  const total = added.lines.every((l) => l.usd !== null) ? added.lines.reduce((s, l) => s + (l.usd ?? 0), 0) : null;
  return (
    <div style={sx('min-width:0;display:flex;flex-direction:column;gap:6px;')} data-testid="finance-plan">
      <div style={sx('display:flex;align-items:baseline;gap:2px 10px;flex-wrap:wrap;padding-bottom:6px;border-bottom:1px solid rgba(34,39,46,.12);min-width:0;')}>
        <span data-testid="pilot-pay-head" style={sx('font-size:16px;font-weight:800;color:#22272E;letter-spacing:-.01em;min-width:0;')}>
          {pilotHead(pilot)}
        </span>
      </div>
      <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>What Aithora made you during the pilot</span>
      <span style={sx('font-size:14px;color:#54606C;')}>This month · same figures as Billing</span>
      <MadeRows lines={added.lines} />
      <span style={sx('display:flex;align-items:baseline;gap:10px;padding:5px 0;font-size:16px;font-variant-numeric:tabular-nums;')}>
        <span style={sx('flex:1 1 auto;color:#22272E;font-weight:800;')}>Total added</span>
        <span data-testid="made-total" style={sx('color:' + (total === null ? '#54606C' : '#1F6B52') + ';font-weight:800;font-size:17px;')}>{total === null ? '–' : $(total)}</span>
      </span>
      {!added.measured && (
        <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>
          These are measured against the months before Aithora once the building’s billing, census and staffing feeds connect. Until then nothing is estimated here.
        </span>
      )}
      <span style={sx('font-size:14px;color:#54606C;')}>Pricing starts when the pilot converts.</span>
    </div>
  );
}

function MadeRows({ lines }: { lines: ReturnType<typeof addedMoney>['lines'] }) {
  return (
    <>
      {lines.map((v) => (
        <span key={v.key} data-testid={`made-${v.key}`} style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.07);font-size:16px;font-variant-numeric:tabular-nums;')}>
          <span style={sx('flex:1 1 180px;min-width:0;color:#22272E;font-weight:700;')}>{v.label}</span>
          <span style={sx('color:' + (v.usd === null ? '#54606C' : '#1F6B52') + ';font-weight:800;')}>{v.usd === null ? NOT_MEASURED : $(v.usd)}</span>
          <span style={sx('flex:1 1 100%;font-size:14px;color:#54606C;line-height:1.4;')}>{v.note}</span>
        </span>
      ))}
    </>
  );
}

const BL_HEAD = 'flex:0 0 auto;display:flex;align-items:baseline;gap:2px 10px;flex-wrap:wrap;padding-bottom:6px;border-bottom:1px solid rgba(34,39,46,.12);min-width:0;';
const BL_TITLE = 'font-size:16px;font-weight:800;color:#22272E;letter-spacing:-.01em;';
const BL_META = 'font-size:14px;color:#54606C;min-width:0;';
const row = (k: string, v: string, color = '#22272E') => (
  <div key={k} style={sx('display:flex;align-items:baseline;gap:10px;padding:3px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
    <span style={sx('font-size:16px;color:#54606C;flex:1 1 auto;min-width:0;')}>{k}</span>
    <span style={sx('font-size:16px;font-weight:700;flex:0 0 auto;color:' + color + ';')}>{v}</span>
  </div>
);

/**
 * Billing's "Your bill" (design blMultiple / blPricing / blBuild, L27413-27425): $0 and when
 * pricing starts during the pilot; after it, occupied beds × $99, the subscription and this
 * month's total from the bill route. The share line appears only when it is switched on.
 */
export function YourBill({
  pilot,
  beds,
  rateUsd,
  subscriptionUsd,
  totalUsd,
  build,
  share,
}: {
  pilot: PilotState | null;
  beds: number | null;
  rateUsd: number | null;
  subscriptionUsd: number | null;
  totalUsd: number | null;
  build: { name: string; note: string; amountCents: number; weight: number }[];
  share: ShareConfig;
}) {
  const inPilot = !!pilot?.inPilot;
  const added = addedMoney();
  const addedTotal = added.lines.every((l) => l.usd !== null) ? added.lines.reduce((s, l) => s + (l.usd ?? 0), 0) : null;
  const shareOn = share.on && !inPilot;
  const shareUsd = shareOn && addedTotal !== null ? Math.round((addedTotal * clampPct(share.pct)) / 100) : null;
  const COLORS = ['#1F6B52', '#2F5C97', '#5B6B7C', '#54606C'];
  return (
    <div data-testid="your-bill" style={sx('display:flex;flex-direction:column;gap:4px;')}>
      <div style={sx('margin-top:6px;')}>
        <div style={sx(BL_HEAD)}>
          <span style={sx(BL_TITLE)}>Your bill</span>
        </div>
      </div>
      <div style={sx('display:flex;align-items:baseline;gap:10px;')}>
        <span data-testid="bill-multiple" style={sx('font-size:24px;font-weight:800;line-height:1;color:#2F5C97;flex:0 0 auto;')}>
          {inPilot ? '$0' : rateUsd !== null ? $(rateUsd) : '–'}
        </span>
        <span style={sx('font-size:16px;color:#3C454F;line-height:1.4;min-width:0;')}>{inPilot ? 'during the free pilot' : 'per occupied bed, per month'}</span>
      </div>
      {inPilot
        ? [
            row('Free pilot', pilot!.day === null ? 'starts at go-live' : `day ${pilot!.day} of ${pilot!.days}`),
            row('You pay', '$0', '#2F5C97'),
            row('Pricing', 'starts when the pilot converts', '#54606C'),
          ]
        : [
            row('Occupied beds this month', beds !== null ? String(beds) : '–'),
            row('Rate', rateUsd !== null ? `${$(rateUsd)} per occupied bed` : '–'),
            row('Subscription', subscriptionUsd !== null ? $(subscriptionUsd) : '–'),
            ...(shareOn ? [row('Share of added money', `${clampPct(share.pct)}% of ${addedTotal === null ? 'added money' : $(addedTotal)} = ${shareUsd === null ? NOT_MEASURED : $(shareUsd)}`)] : []),
            row('This month', totalUsd !== null ? $(totalUsd + (shareUsd ?? 0)) : '–', '#2F5C97'),
          ]}
      <div style={sx('margin-top:6px;')}>
        <div style={sx(BL_HEAD)}>
          <span style={sx(BL_TITLE)}>{inPilot ? 'What Aithora made you during the pilot' : 'How this bill is built'}</span>
          <span style={sx(BL_META)}>{inPilot ? 'this month' : shareOn ? 'subscription · share of added money' : 'subscription'}</span>
        </div>
      </div>
      {inPilot ? (
        <>
          <MadeRows lines={added.lines} />
          <div style={sx('display:flex;align-items:baseline;gap:10px;padding-top:4px;')}>
            <span style={sx('font-size:16px;font-weight:800;color:#22272E;flex:1 1 auto;')}>Total added this month</span>
            <span style={sx('font-size:16px;font-weight:800;color:' + (addedTotal === null ? '#54606C' : '#22272E') + ';font-variant-numeric:tabular-nums;')}>{addedTotal === null ? '–' : $(addedTotal)}</span>
          </div>
        </>
      ) : (
        <>
          <div style={sx('display:flex;height:8px;border-radius:4px;overflow:hidden;')}>
            {build.map((x, i) => (
              <span key={x.name} style={sx('display:block;height:100%;flex:' + x.weight + ' 0 auto;background:' + COLORS[i % COLORS.length] + ';')} />
            ))}
          </div>
          {build.map((x, i) => (
            <div key={x.name} style={sx('display:flex;align-items:center;gap:8px;padding:3px 0;border-bottom:1px solid rgba(34,39,46,.07);min-width:0;')}>
              <span style={sx('width:9px;height:9px;border-radius:3px;flex:0 0 9px;background:' + COLORS[i % COLORS.length] + ';')} />
              <span style={sx('flex:1 1 auto;min-width:0;overflow-wrap:break-word;')} title={x.note}>
                <span style={sx('font-size:16px;color:#22272E;')}>{x.name}</span> <span style={sx('font-size:14px;color:#54606C;')}>· {x.note}</span>
              </span>
              <span style={sx('font-size:16px;font-weight:700;color:#22272E;font-variant-numeric:tabular-nums;')}>{'$' + (x.amountCents / 100).toFixed(2)}</span>
            </div>
          ))}
          <div style={sx('display:flex;align-items:baseline;gap:10px;padding-top:4px;')}>
            <span style={sx('font-size:16px;font-weight:800;color:#22272E;flex:1 1 auto;')}>This period, all in</span>
            <span style={sx('font-size:16px;font-weight:800;color:#22272E;font-variant-numeric:tabular-nums;')}>{totalUsd !== null ? '$' + totalUsd.toFixed(2) : '–'}</span>
          </div>
          {shareOn && <AddedMoneyBlock pct={clampPct(share.pct)} />}
        </>
      )}
    </div>
  );
}

/** Design "Billing · Added money" (blShareShow): shown only with the share switched on, after the pilot. */
function AddedMoneyBlock({ pct }: { pct: number }) {
  const build = useBuildLabels();
  const added = addedMoney();
  return (
    <div data-screen-label="Billing · Added money" style={sx('display:flex;flex-direction:column;gap:2px;margin-top:8px;padding-top:6px;border-top:1px solid rgba(34,39,46,.12);')}>
      <div style={sx('display:flex;align-items:baseline;gap:6px 10px;flex-wrap:wrap;')}>
        <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>Added money this month</span>
        {build && <span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:#8A5A00;')}>MODELED</span>}
      </div>
      {added.lines.map((a) => (
        <div key={a.key} style={sx('display:flex;align-items:baseline;gap:10px;padding:3px 0;border-bottom:1px solid rgba(34,39,46,.07);min-width:0;')}>
          <span style={sx('flex:1 1 auto;min-width:0;')}>
            <span style={sx('font-size:16px;color:#22272E;')}>{a.label}</span> <span style={sx('font-size:14px;color:#54606C;')}>· {a.note}</span>
          </span>
          <span style={sx('font-size:16px;font-weight:700;color:#54606C;font-variant-numeric:tabular-nums;')}>{a.usd === null ? NOT_MEASURED : $(a.usd)}</span>
        </div>
      ))}
      <span style={sx('font-size:16px;color:#3C454F;padding-top:2px;')}>{`Share: ${pct}% of added money, once it is measured`}</span>
    </div>
  );
}

/**
 * Design "Billing · Share settings (Super Admin)" (blShareCtl, L12465): the per-building switch
 * and percent box. Super Admin only; Off by default; off until legal review; applies after the
 * pilot. Facility users never see it (the caller does not render it for them).
 */
export function ShareSettings({ facName, value, onChange }: { facName: string; value: ShareConfig; onChange: (v: ShareConfig) => void }) {
  const [pctText, setPctText] = useState(String(value.pct));
  return (
    <div data-screen-label="Billing · Share settings (Super Admin)" data-testid="share-settings" style={sx('display:flex;flex-direction:column;gap:6px;margin-top:10px;padding:10px 12px;border-radius:10px;background:rgba(34,39,46,.04);border:1px solid rgba(34,39,46,.12);')}>
      <span style={sx('font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#54606C;')}>Share of added money · Super Admin only · applies after the pilot</span>
      <div style={sx('display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;')}>
        <span style={sx('flex:1 1 200px;min-width:0;font-size:16px;font-weight:700;color:#22272E;')}>{facName}</span>
        <button
          type="button"
          role="switch"
          aria-checked={value.on}
          aria-label="Share of added money"
          onClick={() => onChange({ ...value, on: !value.on })}
          style={sx(
            'display:inline-flex;align-items:center;min-height:36px;padding:0 14px;border-radius:8px;font-family:inherit;font-size:16px;font-weight:700;cursor:pointer;' +
              (value.on ? 'background:#2F5C97;color:#fff;border:1px solid #2F5C97;' : 'background:transparent;border:1px solid rgba(34,39,46,.25);color:#22272E;'),
          )}
        >
          {value.on ? 'On' : 'Off'}
        </button>
        <label style={sx('display:inline-flex;align-items:center;gap:6px;font-size:16px;color:#3C454F;')}>
          <input
            type="number"
            min={0}
            max={50}
            step={0.5}
            value={pctText}
            aria-label="Share percent"
            onChange={(e) => {
              setPctText(e.target.value);
              onChange({ ...value, pct: clampPct(e.target.value) });
            }}
            style={sx('width:72px;min-height:36px;box-sizing:border-box;padding:0 8px;border-radius:8px;border:1px solid rgba(34,39,46,.25);background:#FCFAF4;color:#22272E;font:inherit;font-size:16px;')}
          />
          %
        </label>
      </div>
      <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>Off until legal review. The percent is negotiated per building. Facility users see the result on their bill, never these controls.</span>
      {!value.stored && <span data-testid="share-not-stored" style={sx('font-size:14px;color:#8A5A00;line-height:1.4;')}>Not saved yet: the back end does not store this switch, so it resets when you leave the page.</span>}
    </div>
  );
}

export { SHARE_DEFAULT };
